import { useMemo, useState } from 'react'
import { ChevronDownIcon } from 'lucide-react'
import { Link } from 'react-router-dom'

import { plantHref } from '@atbash-labs/plantview-links'
import {
  useGetPlantEquipmentRefsQuery,
  type PlantEquipmentRefsGroup,
  type PMComponentRef
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../../components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../../components/ui/collapsible'
import { cn } from '../../../lib/utils'
import { groupTagPathsByEquipment } from '../util/tag-paths'

/** Spacing/punctuation-insensitive key so a parsed path segment ("Cell57")
 * matches the graph display name of its stored ref ("Cell 57"). */
function nameKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * Equipment-grouped tag-path view. Floor managers think in equipment
 * (CAP-401, PMP-101), not raw `[default]Plant_.../CAP-401/CapsApplied`
 * paths, so the hero summary collapses N paths under their owning
 * equipment id with a comma-separated signal list.
 *
 * Link resolution prefers the config's stored `equipment_refs` — resolved
 * at create/update time by the builder with the SAME graph resolver the
 * runs use — matching each group's parsed segment against a stored ref's
 * name. Groups without a stored match (older configs predate the column)
 * fall back to the render-time graph ladder. Either way the href is built
 * by the shared `plantHref` the insight chips use, and unresolved
 * groups render as plain badges so a broken link is never shown.
 *
 * Used by both RCA (`tag_paths`) and PM (`input_node_ids`) detail
 * pages — the underlying `parseTagPath` handles paths with or without a
 * `[gateway]` bracket prefix so the same component works for both.
 *
 * Raw paths hide behind a Collapsible — animated open/close, no abrupt
 * mount/unmount.
 */
export function EquipmentScope({
  tagPaths,
  equipmentRefs
}: {
  tagPaths: string[]
  /** Builder-resolved refs stored on the config; matched by display name. */
  equipmentRefs?: PMComponentRef[]
}) {
  const groups = useMemo(() => groupTagPathsByEquipment(tagPaths), [tagPaths])
  const [showRaw, setShowRaw] = useState(false)

  const storedByName = useMemo(() => {
    const map = new Map<string, PMComponentRef>()
    for (const ref of equipmentRefs ?? []) {
      const key = ref.name ? nameKey(ref.name) : ''
      if (key && !map.has(key)) map.set(key, ref)
    }
    return map
  }, [equipmentRefs])

  // Render-time fallback for groups without a stored ref. Stable, sorted
  // query arg so the RTK cache key survives re-renders.
  const refGroups = useMemo<PlantEquipmentRefsGroup[]>(
    () =>
      groups
        .flatMap((group) =>
          group.equipmentId !== null && !storedByName.has(nameKey(group.equipmentId))
            ? [
                {
                  name: group.equipmentId,
                  paths: [...new Set(group.paths.map((p) => p.full))].sort()
                }
              ]
            : []
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [groups, storedByName]
  )
  const { data: refs } = useGetPlantEquipmentRefsQuery(refGroups, {
    skip: refGroups.length === 0
  })

  // Null while unresolved (loading, failed, or no matching component).
  const hrefFor = (equipmentId: string | null): string | null => {
    if (equipmentId === null) return null
    const ref = storedByName.get(nameKey(equipmentId)) ?? refs?.[equipmentId]
    return ref ? plantHref(ref.id, componentRefLabel(ref.scope), ref) : null
  }

  if (groups.length === 0) {
    return <p className="m-0 text-sm text-ink-3">No tag paths configured.</p>
  }

  return (
    <div className="flex flex-col gap-2">
      <ul className="m-0 flex flex-col gap-1.5 list-none p-0">
        {groups.map((group) => {
          const equipmentId = group.equipmentId
          const href = hrefFor(equipmentId)
          return (
            <li
              key={equipmentId ?? '__unknown__'}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
            >
              {equipmentId ? (
                href ? (
                  <Badge
                    asChild
                    variant="secondary"
                    className="shrink-0 bg-brand-soft font-mono text-11 tracking-tight text-brand transition-colors hover:bg-brand-soft/80"
                  >
                    <Link to={href}>{equipmentId}</Link>
                  </Badge>
                ) : (
                  <Badge
                    variant="secondary"
                    className="shrink-0 bg-brand-soft font-mono text-11 tracking-tight text-brand"
                  >
                    {equipmentId}
                  </Badge>
                )
              ) : (
                <Badge
                  variant="outline"
                  className="shrink-0 font-mono text-11 tracking-tight text-ink-4"
                >
                  —
                </Badge>
              )}
              <span className="text-xs text-ink-4">
                {group.paths.length} signal{group.paths.length === 1 ? '' : 's'}
              </span>
              <span className="min-w-0 flex-1 text-sm text-ink-2">
                {group.paths.map((p, i) => (
                  <span key={p.full}>
                    {i > 0 && <span className="text-ink-4"> · </span>}
                    {href ? (
                      <Link
                        to={href}
                        className="text-ink-2 underline-offset-2 hover:text-ink hover:underline"
                      >
                        {p.signal}
                      </Link>
                    ) : (
                      <span>{p.signal}</span>
                    )}
                  </span>
                ))}
              </span>
            </li>
          )
        })}
      </ul>

      <Collapsible open={showRaw} onOpenChange={setShowRaw}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="inline-flex w-fit cursor-pointer items-center gap-1 text-11 font-medium uppercase tracking-[0.06em] text-ink-4 transition-colors hover:text-ink-3"
          >
            <ChevronDownIcon
              className={cn('size-3 transition-transform duration-200', showRaw && 'rotate-180')}
            />
            {showRaw ? 'Hide' : 'Show'} raw tag paths
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-wrap gap-1 pt-2">
            {groups.flatMap((g) => {
              const rawHref = hrefFor(g.equipmentId)
              return g.paths.map((p) =>
                rawHref ? (
                  <Badge
                    asChild
                    key={p.full}
                    variant="outline"
                    className="font-mono text-10 transition-colors hover:bg-accent/40"
                  >
                    <Link to={rawHref}>{p.full}</Link>
                  </Badge>
                ) : (
                  <Badge key={p.full} variant="outline" className="font-mono text-10">
                    {p.full}
                  </Badge>
                )
              )
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}

function componentRefLabel(scope: PMComponentRef['scope']): 'Equipment' | 'Line' | 'Tag' {
  if (scope === 'process_line') return 'Line'
  if (scope === 'tag') return 'Tag'
  return 'Equipment'
}
